import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { saveGroup } from '../groups/thunks/saveGroup'
import { fetchUserInfo } from '../user/thunks/fetchUserInfo'
import { dropdownsAdapter } from './dropdownsAdapter'
import { DropdownOption } from './models/DropdownOption'
import { DropdownState } from './models/DropdownState'
import { fetchDropdownData } from './thunks/fetchDropdownData'

type ExtendedDropdownState = {
  loading: Record<string, boolean>
  completed: Record<string, boolean>
  error: Record<string, string | null>
}

export const initialDropdownState = dropdownsAdapter.getInitialState<ExtendedDropdownState>({
  loading: {},
  completed: {},
  error: {},
})

export const dropdownsSlice = createSlice({
  name: 'dropdowns',
  initialState: initialDropdownState,
  reducers: {
    upsertSingleDropdown: (state, action: PayloadAction<DropdownState>) => {
      dropdownsAdapter.upsertOne(state, action.payload)
      state.completed[action.payload.name] = true
    },
    upsertMultipleDropdowns: (state, action: PayloadAction<DropdownState[]>) => {
      dropdownsAdapter.upsertMany(state, action.payload)
      action.payload.forEach((dropdown) => {
        state.completed[dropdown.name] = true
      })
    },
    removeAllDropdowns: (state) => {
      dropdownsAdapter.removeAll(state)
      state.loading = {}
      state.completed = {}
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDropdownData.pending, (state, action) => {
        state.loading[action.meta.arg] = true
        state.error[action.meta.arg] = null
      })
      .addCase(fetchDropdownData.fulfilled, (state, action) => {
        dropdownsAdapter.upsertOne(state, action.payload)
        state.loading[action.meta.arg] = false
        state.completed[action.meta.arg] = true
      })
      .addCase(fetchDropdownData.rejected, (state, action) => {
        state.loading[action.meta.arg] = false
        state.error[action.meta.arg] = action.error.message || 'Failed to fetch'
      })
      .addCase(saveGroup.fulfilled, (state, action) => {
        const groupOptionsState = state.entities.groups

        if (groupOptionsState) {
          const groupOptions: DropdownOption[] = [
            ...groupOptionsState.options,
            {
              label: action.payload.name,
              value: action.payload.id,
            },
          ]
          const newGroupOptionsState: DropdownState = {
            name: 'groups',
            options: groupOptions,
          }

          state.completed.groups = true
          dropdownsAdapter.upsertOne(state, newGroupOptionsState)
        }
      })
      .addCase(fetchUserInfo.fulfilled, (state, action) => {
        const groupOptions: DropdownOption[] = (action?.payload?.groups || []).map(group => ({
          label: group.name as string,
          value: group.id,
        }))
        const groupOptionsState: DropdownState = {
          name: 'groups',
          options: groupOptions,
        }

        state.completed.groups = true
        dropdownsAdapter.upsertOne(state, groupOptionsState)
      })
  },
})

export const {
  upsertSingleDropdown,
  upsertMultipleDropdowns,
  removeAllDropdowns,
} = dropdownsSlice.actions